import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUsers,
  FaBuilding,
  FaHandshake,
  FaBell,
  FaCalendarAlt,
  FaWrench,
  FaTag,
  FaPhoneAlt,
  FaBolt,
  FaEllipsisH,
} from "react-icons/fa";
import { MdElevator } from "react-icons/md";
import { Plus, Search, RefreshCw, CheckCircle, XCircle, Clock, Lightbulb } from "lucide-react";
import AddVisitorModal from "../components/AddVisitorModal";
import { registerPushNotifications, unregisterPushNotifications } from "../pushNotifications";
import {
  getNotifications,
  subscribeNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  getUnreadCount,
  onNotificationTap,
} from "../pushNotifications";
import NotificationPanel from "../components/NotificationPanel";
import ProfileMenu from "../components/ProfileMenu";
import ServiceContacts from "../components/ServiceContacts";
import DIYTutorials from "../components/DIYTutorials";
import ResidentIssues from "../components/ResidentIssues";
import ClubhouseBooking from "../components/ClubhouseBooking";
import ResidentVacateRequest from "../components/ResidentVacateRequest";
import OwnerVacateManagement from "../components/OwnerVacateManagement";
import TrialGate from "../components/TrialGate";
import TrialBanner from "../components/TrialBanner";

import { API_BASE } from '../config'
import { getCommunity } from '../services/onboardingService'
import { formatLocalizedDateTime, localDateString, localDateTimeString, parseDisplayTimestamp } from '../utils/dateUtils'
import { getPortalRoute, normalizeRole } from '../utils/roleRouting'

// --- helpers -----------------------------------------------------------

function decodeIdTokenEmail() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return (JSON.parse(json).email || "").toString().toLowerCase();
  } catch {
    return null;
  }
}

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

function formatVisitorTime(val) {
  if (!val) return "";
  const date = parseDisplayTimestamp(val);
  if (!date) return "";
  return formatLocalizedDateTime(date);
}

function getStatusBadge(status) {
  const badges = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-blue-100 text-blue-800",
    rejected: "bg-red-100 text-red-800",
    "checked-in": "bg-green-100 text-green-800",
    "checked-out": "bg-gray-100 text-gray-800",
    upcoming: "bg-blue-100 text-blue-800",
    completed: "bg-gray-100 text-gray-800",
    cancelled: "bg-red-100 text-red-800",
  };
  const s = (status || "pending").toLowerCase();
  return (
    <span className={`px-2 py-1 rounded-full text-xs ${badges[s] || "bg-gray-100 text-gray-800"}`}>
      {s.charAt(0).toUpperCase() + s.slice(1).replace("-", " ")}
    </span>
  );
}

function mapApiVisitor(it) {
  return {
    id: it.VISITOR_ID || it.visitorId || it.VISIT_ID,
    name: it.NAME || it.name || "",
    visitingUnit: it.UNIT_NBR || it.unit || it.unitNbr || "",
    purpose: it.VISIT_PURPS || it.purpose || it.PURPOSE || "",
    phone: it.PHONE || it.phone || "",
    expectedTime: it.EXPECTED_TIME || it.expectedTime || "",
    checkInTime: it.ENTRY_TIME || it.entryTime || "",
    checkOutTime: it.EXIT_TIME || it.exitTime || "",
    status: (it.STATUS || it.status || "pending").toString().toLowerCase(),
    communityId:
      it.COMMUNITY_ID || it.communityId || it.COMM_VISTRS_PRTN_KEY_ID || "",
  };
}

// --- StatusCard --------------------------------------------------------

function StatusCard({ title, value, icon, status = "good" }) {
  const colors = {
    good: "bg-green-50 border-green-200",
    warning: "bg-yellow-50 border-yellow-200",
    error: "bg-red-50 border-red-200",
  };
  return (
    <div className={`p-4 rounded-lg border-2 ${colors[status]}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm">{title}</p>
          <p className="mt-2 font-semibold text-lg">{value}</p>
        </div>
        <div className="text-gray-700">{icon}</div>
      </div>
    </div>
  );
}

// =======================================================================
// ResidentPortal
// =======================================================================

export default function ResidentPortal() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [user, setUser] = useState(null);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [communityPlan, setCommunityPlan] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const communityId = sessionStorage.getItem('community_id') || '';
  const isTrial = communityPlan === '' ? undefined : communityPlan === 'TRIAL';
  const [visitors, setVisitors] = useState([]);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFilter, setDateFilter] = useState(
    localDateString()
  );
  const [devices, setDevices] = useState([]);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState(() => getNotifications());
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const unitNotifications = notifications.filter((n) => n.data?.type !== "announcement");
  const unreadCount = unitNotifications.filter((n) => !n.read).length;

  // ---- auth guard & user record ----------------------------------------

  useEffect(() => {
    (async () => {
      try {
        const idToken = sessionStorage.getItem("id_token");
        if (!idToken) {
          navigate("/");
          return;
        }

        const email = decodeIdTokenEmail();
        if (!email) {
          sessionStorage.removeItem("id_token");
          navigate("/access-denied");
          return;
        }

        let data = await apiFetch(
          `/reg-user?emailId=${encodeURIComponent(email)}`
        );
        const returned = (
          (data.emailId || data.email || data.Email || data.EMAIL) || ""
        )
          .toString()
          .toLowerCase();
        if (returned !== email) {
          sessionStorage.removeItem("id_token");
          navigate("/access-denied");
          return;
        }

        // Multi-community: resolve the record that was selected in the community picker
        if (data.multiCommunity && Array.isArray(data.communities)) {
          const selectedCid = sessionStorage.getItem('community_id') || '';
          const selectedUid = sessionStorage.getItem('user_id') || '';
          const match = data.communities.find(
            c => {
              const cid = c.COMMUNITY_ID || c.communityId || '';
              const uid = c.USER_ID || c.userId || '';
              return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid);
            }
          ) || data.communities[0];
          if (!match) { navigate('/access-denied'); return; }
          data = match;
        }

        // persist useful session values
        const cid =
          data.COMMUNITY_ID || data.communityId || data.community_id || "";
        if (cid) sessionStorage.setItem("community_id", cid);
        const cname = data.COMMUNITY_NAME || data.communityName || "";
        if (cname) sessionStorage.setItem("community_name", cname);
        const unitNbr =
          data.UNIT_NBR || data.unitNbr || data.unit_nbr || data.unit || "";
        if (unitNbr) sessionStorage.setItem("unit_nbr", unitNbr);
        const userId = data.USER_ID || data.userId || "";
        if (userId) sessionStorage.setItem("user_id", userId);
        const role = normalizeRole(data.ROLE || data.role);
        const expectedRoute = getPortalRoute(data, { fallback: '/access-denied' });

        // Strict role guard: ResidentPortal allows resident and non-resident owner only.
        if (expectedRoute !== '/resident') {
          navigate(expectedRoute);
          return;
        }

        if (role) sessionStorage.setItem("user_role", role);

        setUser(data);

        // Fetch community feature flags for trial gating
        if (cid) {
          getCommunity({ communityId: cid }).then(comm => {
            if (comm?.FEATURE_FLAGS) setFeatureFlags(comm.FEATURE_FLAGS)
            if (comm?.PLAN) setCommunityPlan((comm.PLAN || '').toUpperCase())
          }).catch(() => {})
        }

        // Register push notifications
        if (cid && unitNbr) {
          registerPushNotifications({ communityId: cid, unitNbr, email }).catch(
            (err) => console.error('Push registration error:', err)
          );
        } else {
          console.warn('Push: skipped — missing communityId or unitNbr', { cid, unitNbr });
        }
      } catch (err) {
        console.error("ResidentPortal auth check failed", err);
        sessionStorage.removeItem("id_token");
        navigate("/access-denied");
      }
    })();
  }, [navigate]);

  // ---- refresh community feature flags on focus / periodically --------
  useEffect(() => {
    if (!communityId) return
    let mounted = true
    const refreshCommunity = async () => {
      try {
        const comm = await getCommunity({ communityId })
        if (!mounted) return
        if (comm?.FEATURE_FLAGS) setFeatureFlags(comm.FEATURE_FLAGS)
        if (comm?.PLAN) setCommunityPlan((comm.PLAN || '').toUpperCase())
      } catch (err) {
        // ignore transient refresh failures
      }
    }
    window.addEventListener('focus', refreshCommunity)
    const interval = setInterval(refreshCommunity, 15000)
    return () => {
      mounted = false
      window.removeEventListener('focus', refreshCommunity)
      clearInterval(interval)
    }
  }, [communityId])

  // ---- notification store subscription --------------------------------
  useEffect(() => {
    const unsub = subscribeNotifications((list) => setNotifications([...list]));
    const unsubTap = onNotificationTap((notifData) => {
      setNotifPanelOpen(true);
      // If tapped from OS, navigate directly without opening panel
      if (notifData?.data?.type) {
        _handleNotifNavigate({ data: notifData.data });
      }
    });
    return () => { unsub(); unsubTap(); };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ---- navigate to section based on notification type -----------------
  function _handleNotifNavigate(notification) {
    const type = notification?.data?.type;
    if (type === 'visitor') {
      setActiveTab('visitors');
      setShowMoreMenu(false);
    } else if (type === 'booking') {
      setActiveTab('bookings');
      setShowMoreMenu(false);
    } else if (type === 'announcement') {
      setActiveTab('updates');
      setShowMoreMenu(false);
    }
    // entry-exit type is management-only, no action on resident side
  }

  // ---- load visitors for this unit -------------------------------------

  async function loadVisitors() {
    if (!user) return;
    const role = normalizeRole(user.ROLE || user.role || '');
    if (role === 'non-resident') {
      setVisitors([]);
      return;
    }
    setLoadingVisitors(true);
    const unit =
      user.UNIT_NBR || user.unitNbr || user.unit_nbr || user.unit || "";
    const community =
      user.COMMUNITY_ID ||
      user.communityId ||
      user.community_id ||
      user.COMM_VISTRS_PRTN_KEY_ID ||
      "";
    const params = new URLSearchParams();
    if (unit) params.set("unitNbr", unit);
    if (community) params.set("communityId", community);
    if (dateFilter) params.set("date", dateFilter);

    try {
      const data = await apiFetch(`/visitors?${params.toString()}`);
      setVisitors((Array.isArray(data) ? data : []).map(mapApiVisitor));
    } catch (e) {
      console.error("loadVisitors error", e);
      setVisitors([]);
    }
    setLoadingVisitors(false);
  }

  useEffect(() => {
    if (user) loadVisitors();
  }, [user, dateFilter]);

  // ---- load devices for this community ---------------------------------

  async function loadDevices() {
    if (!user) return;
    const community =
      user.COMMUNITY_ID ||
      user.communityId ||
      user.community_id ||
      user.COMM_VISTRS_PRTN_KEY_ID ||
      "";
    if (!community) return;
    try {
      const data = await apiFetch(
        `/devices?communityId=${encodeURIComponent(community)}`
      );
      setDevices(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("loadDevices error", e);
      setDevices([]);
    }
  }

  useEffect(() => {
    if (user) loadDevices();
  }, [user]);

  // ---- toast -----------------------------------------------------------

  function showToast(message, type = "success") {
    const id = Date.now().toString() + Math.random().toString(36).slice(2, 7);
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }

  // ---- announcements ---------------------------------------------------

  async function loadAnnouncements() {
    const cid = user?.COMMUNITY_ID || user?.communityId || sessionStorage.getItem("community_id") || "";
    if (!cid) return;
    setLoadingAnnouncements(true);
    try {
      const data = await apiFetch(`/announcements?communityId=${encodeURIComponent(cid)}`);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("loadAnnouncements error", e);
    }
    setLoadingAnnouncements(false);
  }

  useEffect(() => {
    if (user) loadAnnouncements();
  }, [user]);

  // ---- visitor create --------------------------------------------------

  async function handleCreateVisitor(formData) {
    const community =
      user?.COMMUNITY_ID ||
      user?.communityId ||
      user?.COMM_VISTRS_PRTN_KEY_ID ||
      "";
    if (!community) {
      showToast("Missing community id", "error");
      return;
    }
    const unit =
      user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || "";
    const expected = formData.expectedTime || localDateTimeString();

    try {
      await apiFetch("/visitors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          COMMUNITY_ID: community,
          UNIT_NBR: formData.visitingUnit || unit,
          EXPECTED_TIME: expected,
          VISIT_PURPS: formData.purpose,
          PHONE: formData.phone || "",
          NAME: formData.name,
          STATUS: "approved",
        }),
      });
      showToast("Visitor pre-approved", "success");
      setShowAddModal(false);
      await loadVisitors();
    } catch (e) {
      console.error("create visitor error", e);
      showToast("Failed to create visitor", "error");
    }
  }

  // ---- approve visitor -------------------------------------------------

  async function handleApproveVisitor(id) {
    const v = visitors.find((x) => x.id === id);
    if (!v) return;
    const unit =
      user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || "";
    const role = user?.ROLE || user?.role || "";
    try {
      await apiFetch("/visitors", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          COMMUNITY_ID: v.communityId,
          VISITOR_ID: id,
          ACTION_BY: `${unit}_${role}`,
          STATUS: "APPROVED",
        }),
      });
      setVisitors((prev) =>
        prev.map((x) => (x.id === id ? { ...x, status: "approved" } : x))
      );
      showToast("Visitor approved", "success");
    } catch {
      showToast("Failed to approve visitor", "error");
    }
  }

  // ---- reject visitor --------------------------------------------------

  async function handleRejectVisitor(id) {
    const v = visitors.find((x) => x.id === id);
    if (!v) return;
    const unit =
      user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || "";
    const role = user?.ROLE || user?.role || "";
    try {
      await apiFetch("/visitors", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          COMMUNITY_ID: v.communityId,
          VISITOR_ID: id,
          ACTION_BY: `${unit}_${role}`,
          STATUS: "REJECTED",
        }),
      });
      setVisitors((prev) =>
        prev.map((x) => (x.id === id ? { ...x, status: "rejected" } : x))
      );
      showToast("Visitor rejected", "success");
    } catch {
      showToast("Failed to reject visitor", "error");
    }
  }

  // ---- cancel (set status = REJECTED) ----------------------------------

  async function handleCancelVisitor(id) {
    const v = visitors.find((x) => x.id === id);
    if (!v) return;
    const unit =
      user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || "";
    const role = user?.ROLE || user?.role || "";
    try {
      await apiFetch("/visitors", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          COMMUNITY_ID: v.communityId,
          VISITOR_ID: id,
          ACTION_BY: `${unit}_${role}`,
          STATUS: "REJECTED",
        }),
      });
      setVisitors((prev) =>
        prev.map((x) => (x.id === id ? { ...x, status: "cancelled" } : x))
      );
      showToast("Visitor cancelled", "success");
    } catch {
      showToast("Failed to cancel visitor", "error");
    }
  }

  // ---- sign-out --------------------------------------------------------

  async function handleSignOut() {
    let token =
      sessionStorage.getItem("access_token") ||
      sessionStorage.getItem("id_token");
    if (token) {
      try {
        await fetch("https://oauth2.googleapis.com/revoke", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: `token=${encodeURIComponent(token)}`,
        });
      } catch {}
    }
    unregisterPushNotifications().catch(() => {});
    sessionStorage.removeItem("id_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("push_token");
    sessionStorage.removeItem("community_id");
    sessionStorage.removeItem("unit_nbr");
    sessionStorage.removeItem("user_id");
    sessionStorage.removeItem("user_role");
    navigate("/scm/login");
  }

  // ---- derived data ----------------------------------------------------

  const unitNbr =
    user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || "";
  const userName = (() => {
    const first = (user?.FIRST_NM || user?.first_nm || user?.firstName || "").toString().trim();
    const last = (user?.LAST_NM || user?.last_nm || user?.lastName || "").toString().trim();
    if (first || last) return [first, last].filter(Boolean).join(' ');
    return user?.NAME || user?.name || decodeIdTokenEmail()?.split("@")[0] || "Resident";
  })();

  const userEmail = decodeIdTokenEmail() || '';
  const userRole = normalizeRole(user?.ROLE || user?.role || sessionStorage.getItem('user_role') || '');
  const userType = (user?.USER_TYPE || user?.userType || user?.user_type || '').toString().toLowerCase();
  // Non-resident users are owners who have let out their unit.
  const isNonResidentOwner = userRole === 'non-resident';
  const communityName = user?.COMMUNITY_NAME || user?.communityName || sessionStorage.getItem('community_name') || '';

  const upcomingVisitors = visitors.filter(
    (v) => v.status === "pending" || v.status === "approved"
  ).length;

  const checkedInVisitors = visitors.filter(
    (v) => v.status === "checked-in"
  ).length;

  const totalVisitorsToday = visitors.length;

  // ---- tabs config (same structure as ResidentApp) ----------------------

  const allTabs = [
    { id: "home", label: "Home", icon: FaHome },
    { id: "visitors", label: "Visitors", icon: FaUsers },
    { id: "bookings", label: "Bookings", icon: FaCalendarAlt },
    { id: "issues", label: "Issues", icon: FaWrench },
    { id: "vacate", label: "Vacate", icon: FaHome },
    { id: "updates", label: "Updates", icon: FaBell },
    { id: "deals", label: "Deals", icon: FaTag },
    { id: "diy", label: "DIY", icon: FaBolt },
    { id: "properties", label: "Properties", icon: FaBuilding },
    { id: "contacts", label: "Contacts", icon: FaPhoneAlt },
  ];
  const tabs = allTabs;

  const renderOwnerPrivacyNotice = (title) => (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-3">{title}</h2>
      <div className="bg-white border border-amber-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-amber-800">Privacy protected</p>
        <p className="text-sm text-amber-700 mt-2">
          For privacy of resident these details are not visible / accessible for owners who let out their unit.
        </p>
      </div>
    </div>
  );

  // ---- filtered visitors for list view ---------------------------------

  const filteredVisitors = visitors.filter((v) => {
    const matchSearch =
      (v.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (v.visitingUnit || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = filterStatus === "all" || v.status === filterStatus;
    return matchSearch && matchFilter;
  });

  // ---- render content per tab ------------------------------------------

  function renderContent() {
    if (!user) {
      return (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Loading...</p>
          </div>
        </div>
      )
    }
    switch (activeTab) {
      // ================================================================
      // HOME — dashboard with live stats
      // ================================================================
      case "home":
        return (
          <div className="p-4">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg p-6 mb-6">
              <h1 className="mb-1 text-white text-xl font-bold">
                Welcome back, {userName}!
              </h1>
              <p className="text-blue-100">Unit {unitNbr}</p>
            </div>

            {/* Quick Stats — Overview */}
            <div className="mb-6">
              <h2 className="font-bold text-lg mb-3">Overview</h2>
              <div className="grid grid-cols-2 gap-3">
                {!isNonResidentOwner && (
                  <>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FaUsers size={18} className="text-blue-600" />
                        <span className="text-sm text-gray-600">Visitors Today</span>
                      </div>
                      <p className="text-2xl font-bold">{upcomingVisitors}</p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FaUsers size={18} className="text-green-600" />
                        <span className="text-sm text-gray-600">Checked-in Now</span>
                      </div>
                      <p className="text-2xl font-bold">{checkedInVisitors}</p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FaCalendarAlt size={18} className="text-orange-600" />
                        <span className="text-sm text-gray-600">Total Today</span>
                      </div>
                      <p className="text-2xl font-bold">{totalVisitorsToday}</p>
                    </div>
                  </>
                )}

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FaHome size={18} className="text-blue-600" />
                    <span className="text-sm text-gray-600">My Unit</span>
                  </div>
                  <p className="text-2xl font-bold">{unitNbr || "—"}</p>
                </div>

                {isNonResidentOwner && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FaTag size={18} className="text-amber-600" />
                      <span className="text-sm text-gray-600">Status</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">Let Out</p>
                  </div>
                )}
              </div>
            </div>

            {/* Equipment Status */}
            {devices.length > 0 && (() => {
              const getStatus = (d) =>
                (d.STATUS || d.status || "offline").toString().toLowerCase();
              const getType = (d) =>
                (d.DEVICE_TYPE || d.deviceType || d.device_type || "").toString().toLowerCase();
              const getName = (d) =>
                d.DEVICE_NAME || d.deviceName || d.device_name || "Device";
              const getDetails = (d) =>
                d.DETAILS || d.details || d.LAST_UPDATED || d.lastUpdated || "";

              const lifts = devices.filter((d) => {
                const t = getType(d);
                return t === "lift" || t === "elevator";
              });
              const generators = devices.filter((d) => getType(d) === "generator");
              const lighting = devices.filter((d) => {
                const t = getType(d);
                return t === "lighting" || t === "light" || t === "common area lighting";
              });
              const otherDevices = devices.filter((d) => {
                const t = getType(d);
                return t !== "lift" && t !== "elevator" && t !== "generator" && t !== "lighting" && t !== "light" && t !== "common area lighting";
              });

              const statusBadge = (s) => {
                const cls =
                  s === "operational"
                    ? "bg-green-100 text-green-800"
                    : s === "maintenance"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800";
                const label =
                  s === "operational" ? "● Online" : s === "maintenance" ? "● Maintenance" : "● Offline";
                return (
                  <span className={`px-3 py-1 rounded-full text-xs ${cls}`}>
                    {label}
                  </span>
                );
              };

              return (
                <div className="mb-6">
                  <h2 className="font-bold text-lg mb-3">Equipment Status</h2>
                  <div className="space-y-3">
                    {/* Generators */}
                    {generators.map((d, i) => (
                      <div key={`gen-${i}`} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FaBolt size={18} className="text-yellow-600" />
                            <span className="text-sm">{getName(d)}</span>
                          </div>
                          {statusBadge(getStatus(d))}
                        </div>
                        {getDetails(d) && (
                          <p className="text-xs text-gray-600">{getDetails(d)}</p>
                        )}
                      </div>
                    ))}

                    {/* Lighting */}
                    {lighting.map((d, i) => (
                      <div key={`light-${i}`} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Lightbulb size={18} className="text-yellow-600" />
                            <span className="text-sm">{getName(d)}</span>
                          </div>
                          {statusBadge(getStatus(d))}
                        </div>
                        {getDetails(d) && (
                          <p className="text-xs text-gray-600">{getDetails(d)}</p>
                        )}
                      </div>
                    ))}

                    {/* Elevators / Lifts */}
                    {lifts.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <MdElevator size={20} className="text-blue-600" />
                          <span className="text-sm">Elevators</span>
                        </div>
                        <div className="space-y-2">
                          {lifts.map((d, i) => (
                            <div key={`lift-${i}`} className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">{getName(d)}</span>
                              {statusBadge(getStatus(d))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Other devices */}
                    {otherDevices.map((d, i) => (
                      <div key={`other-${i}`} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FaWrench size={18} className="text-gray-600" />
                            <span className="text-sm">{getName(d)}</span>
                          </div>
                          {statusBadge(getStatus(d))}
                        </div>
                        {getDetails(d) && (
                          <p className="text-xs text-gray-600">{getDetails(d)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );

      // ================================================================
      // VISITORS — list with real API data
      // ================================================================
      case "visitors":
        if (isNonResidentOwner) {
          return renderOwnerPrivacyNotice("My Visitors");
        }
        return (
          <div className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="text-lg font-bold">My Visitors</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadVisitors}
                  disabled={loadingVisitors}
                  className="p-2 text-gray-500 rounded-lg bg-gray-100"
                >
                  <RefreshCw
                    size={18}
                    className={loadingVisitors ? "animate-spin" : ""}
                  />
                </button>
              </div>
            </div>

            {/* Pre-Approve button — trial: limit per day */}
            {(() => {
              const today = localDateString()
              // -1 (or any negative) means "unlimited" — used by PAID plans; only TRIAL plans enforce a real limit.
              const maxPreApprove = featureFlags?.max_preapprove_day
              const limited = maxPreApprove != null && maxPreApprove >= 0
              const todayCount = limited
                ? visitors.filter(v => {
                    const vDate = (v.expectedTime || '').slice(0, 10)
                    return vDate === today && (v.status === 'pending' || v.status === 'approved')
                  }).length
                : 0
              const preApproveAtLimit = limited && todayCount >= maxPreApprove
              return (
                <>
                  {limited && (
                    <div style={{
                      background: preApproveAtLimit ? '#fef2f2' : '#fffbeb',
                      border: `1px solid ${preApproveAtLimit ? '#fca5a5' : '#fbbf24'}`,
                      borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 8,
                      color: preApproveAtLimit ? '#991b1b' : '#92400e'
                    }}>
                      {preApproveAtLimit
                        ? `Trial limit: ${maxPreApprove} pre-approvals/day reached. Upgrade to add more.`
                        : `Trial: ${todayCount} / ${maxPreApprove} pre-approvals used today`}
                    </div>
                  )}
                  <button
                    onClick={() => !preApproveAtLimit && setShowAddModal(true)}
                    disabled={preApproveAtLimit}
                    className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 mb-3 ${preApproveAtLimit ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white'}`}
                  >
                    <Plus size={18} /> Pre-Approve a Visitor
                  </button>
                </>
              )
            })()}

            {/* Date filter */}
            <div className="relative mb-3">
              <FaCalendarAlt size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full block box-border border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm bg-white shadow-sm appearance-none"
                style={{ WebkitAppearance: 'none', minWidth: 0 }}
              />
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search visitors…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-white shadow-sm"
              />
            </div>

            {/* Status filter pills */}
            <div className="grid grid-cols-2 gap-2 mb-4 sm:flex sm:flex-wrap sm:gap-2">
              {["all", "pending", "approved", "checked-in"].map((s) => {
                const statusLabel = s === "checked-in" ? "Checked in" : s.charAt(0).toUpperCase() + s.slice(1)
                return (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={`w-full sm:w-auto px-2.5 sm:px-3 py-2 rounded-lg text-[13px] sm:text-sm leading-tight text-center whitespace-nowrap ${
                      filterStatus === s
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {statusLabel}
                  </button>
                )
              })}
            </div>

            {/* Visitor cards */}
            <div className="space-y-3">
              {loadingVisitors && <div className="text-white-500">Loading visitors…</div>}
              {!loadingVisitors && filteredVisitors.length === 0 && (
                <div className="text-white font-medium text-center py-8">No visitors found for selected date</div>
              )}
              {!loadingVisitors &&
                filteredVisitors.map((v) => (
                  <div
                    key={v.id}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium mb-1">{v.name}</h3>
                        <p className="text-gray-600 text-sm">{v.phone}</p>
                      </div>
                      {getStatusBadge(v.status)}
                    </div>

                    <div className="space-y-1 mb-3">
                      <p className="text-sm text-gray-600">
                        <span className="text-gray-900 font-medium">Unit:</span>{" "}
                        {v.visitingUnit}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="text-gray-900 font-medium">Purpose:</span>{" "}
                        {v.purpose}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="text-gray-900 font-medium">Expected:</span>{" "}
                        {formatVisitorTime(v.expectedTime)}
                      </p>
                      {v.checkInTime && (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-900 font-medium">Check-in:</span>{" "}
                          {formatVisitorTime(v.checkInTime)}
                        </p>
                      )}
                      {v.checkOutTime && (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-900 font-medium">Check-out:</span>{" "}
                          {formatVisitorTime(v.checkOutTime)}
                        </p>
                      )}
                    </div>

                    {/* Approve / Reject buttons for pending visitors */}
                    {v.status === "pending" && !v.checkInTime && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveVisitor(v.id)}
                          className="flex-1 bg-green-50 text-green-600 border border-green-200 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                        >
                          <CheckCircle size={16} /> Approve
                        </button>
                        <button
                          onClick={() => handleRejectVisitor(v.id)}
                          className="flex-1 bg-red-50 text-red-600 border border-red-200 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                        >
                          <XCircle size={16} /> Reject
                        </button>
                      </div>
                    )}

                    {/* Cancel button for approved visitors */}
                    {v.status === "approved" && !v.checkInTime && (
                      <button
                        onClick={() => handleCancelVisitor(v.id)}
                        className="w-full bg-red-50 text-red-600 border border-red-200 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                      >
                        <XCircle size={16} /> Cancel Visitor
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        );

      // ================================================================
      // Coming-soon tabs
      // ================================================================
      case "bookings":
        if (isNonResidentOwner) {
          return renderOwnerPrivacyNotice("Bookings");
        }
        return <ClubhouseBooking trialMaxPerMonth={featureFlags?.max_bookings_month ?? null} />;
      case "issues":
        if (isNonResidentOwner) {
          return renderOwnerPrivacyNotice("Issues");
        }
        return <ResidentIssues />;
      case "vacate":
        return (
          <TrialGate feature="resident_vacate" featureFlags={featureFlags} label="Vacate Request">
            {(isNonResidentOwner || userType === "owner")
              ? <OwnerVacateManagement unitNbr={unitNbr} communityId={user?.COMMUNITY_ID || user?.communityId || ""} />
              : <ResidentVacateRequest unitNbr={unitNbr} />}
          </TrialGate>
        );
      case "updates":
        return (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Community Updates</h2>
              <button onClick={loadAnnouncements} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
                <RefreshCw size={16} className={loadingAnnouncements ? "animate-spin" : ""} />
              </button>
            </div>
            {loadingAnnouncements && announcements.length === 0 ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FaBolt size={28} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No announcements yet</p>
                <p className="text-xs mt-1">Community announcements will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {announcements.map((a) => (
                  <div
                    key={a.ANNOUNCEMENT_ID}
                    className="bg-white border border-gray-200 rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-sm">{a.TITLE}</h3>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                        {a.CREATED_AT ? new Date(a.CREATED_AT).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{a.BODY}</p>
                    {a.CREATED_BY && <p className="text-xs text-gray-400 mt-2">— {a.CREATED_BY}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case "deals":
        return (
          <div className="p-4">
              <h2 className="text-lg font-bold">Deals</h2>
              <p className="text-sm text-gray-500">Local offers and advertisements.</p>
              <p className="text-sm font-bold text-red-400 mt-1">Coming Soon in Beta 2.0</p>
          </div>
        );
      case "properties":
        return (
          <div className="p-4">
              <h2 className="text-lg font-bold">Properties</h2>
              <p className="text-sm text-gray-500">Rentals, sales and listings.</p>
              <p className="text-sm font-bold text-red-400 mt-1">Coming Soon in Beta 2.0</p>
          </div>
        );
      case "contacts":
        return <ServiceContacts />;
      case "diy":
        return <DIYTutorials />;
      default:
        return null;
    }
  }

  // ---- main render -----------------------------------------------------

  return (
    <div className="min-h-screen portal-bg">
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 safe-right">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-2 rounded shadow text-sm max-w-[90vw] ${
              t.type === "success"
                ? "bg-green-600 text-white"
                : "bg-red-600 text-white"
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-4 sticky top-0 z-50 safe-top safe-left safe-right">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <FaHome size={24} className="flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-semibold truncate">
                {communityName || 'Smart Community'}
              </h1>
              <p className="text-xs text-blue-100">{communityName ? 'Powered by Smart Community Manager' : 'Resident Portal'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setNotifPanelOpen(true)}
              className="relative bg-white/20 hover:bg-white/30 active:bg-white/40 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0 text-white"
            >
              <FaBell size={18} className="block" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center font-bold">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <ProfileMenu
              name={userName}
              email={userEmail}
              role={userRole}
              unitNbr={unitNbr}
              onSignOut={handleSignOut}
            />
          </div>
        </div>
      </div>

      {/* Trial plan banner — shown only when community is on TRIAL plan (read-only for residents) */}
      <TrialBanner communityId={sessionStorage.getItem('community_id') || ''} />

      {/* Content */}
      <div
        className="max-w-2xl mx-auto w-full px-1 sm:px-0"
        style={{
          paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {renderContent()}
      </div>

      {/* Add Visitor Modal */}
      <AddVisitorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateVisitor}
        defaultUnit={unitNbr}
        unitReadOnly
      />

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={notifPanelOpen}
        onClose={() => setNotifPanelOpen(false)}
        notifications={unitNotifications}
        onMarkRead={(id) => markNotificationRead(id)}
        onMarkAllRead={() => markAllNotificationsRead()}
        onClear={() => clearAllNotifications()}
        onNotificationTap={_handleNotifNavigate}
      />

      {/* Bottom Nav — 4 primary tabs + More */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t z-50 safe-left safe-right"
        style={{ paddingBottom: "calc(0.25rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="max-w-2xl mx-auto flex justify-around">
          {tabs.slice(0, 4).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const badge =
              tab.id === "visitors" && upcomingVisitors > 0
                ? upcomingVisitors
                : null;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === "home") setDateFilter(localDateString());
                  setShowMoreMenu(false);
                  setActiveTab(tab.id);
                }}
                className={`relative flex-1 py-3 flex flex-col items-center text-xs ${
                  isActive ? "text-blue-600 font-semibold" : "text-gray-500"
                }`}
              >
                <Icon size={20} />
                <span className="mt-1 whitespace-nowrap">{tab.label}</span>
                {badge && (
                  <span className="absolute top-1 right-2 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
          {/* More button */}
          <button
            onClick={() => setShowMoreMenu((v) => !v)}
            className={`flex-1 py-3 flex flex-col items-center text-xs ${
              showMoreMenu || tabs.slice(4).some((t) => t.id === activeTab)
                ? "text-blue-600 font-semibold"
                : "text-gray-500"
            }`}
          >
            <FaEllipsisH size={20} />
            <span className="mt-1 whitespace-nowrap">More</span>
          </button>
        </div>
      </div>

      {/* More menu overlay */}
      {showMoreMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
          <div
            className="fixed left-0 right-0 z-50 safe-left safe-right flex justify-center"
            style={{ bottom: "calc(60px + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="bg-white rounded-t-2xl shadow-lg border max-w-2xl w-full mx-4 p-4">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {tabs.slice(4).map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => { setShowMoreMenu(false); setActiveTab(tab.id); }}
                      className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs ${
                        isActive ? "text-blue-600 font-semibold bg-blue-50" : "text-gray-600"
                      }`}
                    >
                      <Icon size={22} />
                      <span className="whitespace-nowrap">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
